import CheckIcon from '@mui/icons-material/Check'
import StarIcon from '@mui/icons-material/Star'
import Box from '@mui/material/Box/Box'
import Skeleton from '@mui/material/Skeleton/Skeleton'
import Stack from '@mui/material/Stack/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { RoomSelector } from '@components/room_selector/room_selector'
import Divider from '@mui/material/Divider/Divider'
import { useBookingContext } from '@providers/booking/booking_context'
import { i18WithParams as t } from '@shared/locale'
import { useClientBookingSearch } from 'service-api/src/hooks/use_client_booking_search'
import { BookingSearchClientDto } from 'service-api/src/types/api'
import { buildSearchParams } from './helper'
import { RequiredAddon } from './required_addon'
import { TBookingSearchOptions } from 'service-api/src/types/general'

const SkeletonLoader = () => {
  return (
    <Box p={2}>
      <Skeleton height={30} variant="rounded" sx={{ mb: 1 }} />
      <Skeleton height={30} variant="rounded" sx={{ mb: 1 }} />
      <Skeleton height={30} variant="rounded" sx={{ mb: 1 }} />
      <Skeleton height={30} variant="rounded" sx={{ mb: 1 }} />
    </Box>
  )
}

interface TRequiredAddOnsInnerProps {
  offers?: BookingSearchClientDto[]
  petIndex: number
  onChangeTab: (_: string, newValue: number) => void
}

export const RequiredAddonsInner = ({ offers, petIndex, onChangeTab }: TRequiredAddOnsInnerProps) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const groupIdx = bookingCtx.state.currentGroup

  const changeRequiredAddonHandler = useCallback(
    (args: unknown) => {
      bookingCtx.dispatch({ type: 'SET_REQUIRED_ADDON', payload: { value: args, groupIdx, petIdx: petIndex } })
    },
    [bookingCtx, groupIdx, petIndex]
  )

  const tabs = useMemo(() => {
    const group = bookingCtx.state.cart.groups[groupIdx]
    return group.pets.map((pet, idx) => ({
      label: pet.name,
      room: '',
      icon: group.requiredAddOns?.[idx] ? <CheckIcon fontSize="small" /> : undefined,
    }))
  }, [bookingCtx.state.cart.groups, groupIdx])

  if (!offers) {
    return null
  }

  return (
    <Box mt={2}>
      {isBelowMD && <Divider sx={{ my: 3 }} />}
      <Stack direction="row" gap={1} alignItems="center" mb={2}>
        <StarIcon color="primary" fontSize="large" />
        <Typography variant="h5" color="text.primary">
          {t('searchResults.requiredAddons.select')}
        </Typography>
      </Stack>
      {tabs.length > 1 && <RoomSelector tabs={tabs} tabIndex={petIndex} onChange={onChangeTab} fullWidth={false} />}
      <Stack direction="column" gap={2}>
        {offers.map((offer, idx) => (
          <Fragment key={offer.offer?.id || idx}>
            <RequiredAddon
              key={offer.offer?.id || idx}
              offer={offer}
              groupIndex={bookingCtx.state.currentGroup}
              petIndex={petIndex}
              onChange={changeRequiredAddonHandler}
            />
            {idx < offers.length - 1 && <Divider />}
          </Fragment>
        ))}
      </Stack>
    </Box>
  )
}

interface SelectRequiredAddonsProps {
  petIndex?: number
  onChangeTab?: (_: string, newValue: number) => void
}

export const SelectRequiredAddons = ({
  petIndex: propPetIndex,
  onChangeTab: propOnChangeTab,
}: SelectRequiredAddonsProps = {}) => {
  const bookingCtx = useBookingContext()
  const [internalPetIndex, setInternalPetIndex] = useState(0)
  const { accountName: locationName = '' } = useParams()
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const tabIndex = bookingCtx.state.currentGroup

  const { data, isLoading, isSuccess } = useClientBookingSearch(locationName, serviceTypeName, {
    ...(buildSearchParams(bookingCtx) as TBookingSearchOptions),
    required: true,
    offersType: 'SERVICE',
    cacheEnabled: true,
  })

  const offers = useMemo(() => {
    if (isSuccess && data?.results) {
      return data.results.sort((a, b) => {
        const availableA = a.availabilityGroups?.[tabIndex]?.availabilities?.[0]?.available
        const availableB = b.availabilityGroups?.[tabIndex]?.availabilities?.[0]?.available

        // If one is unavailable and the other is available, sort unavailable last
        if (availableA === false && availableB !== false) return 1
        if (availableB === false && availableA !== false) return -1

        // If both have same availability status, sort by price
        const priceA = a.availabilityGroups?.[0]?.availabilities?.[tabIndex]?.price?.rate || 0
        const priceB = b.availabilityGroups?.[0]?.availabilities?.[tabIndex]?.price?.rate || 0
        return priceA - priceB
      })
    }
    return []
  }, [data?.results, isSuccess, tabIndex])

  const petIndex = propPetIndex ?? internalPetIndex

  const onChangeTabHandler = useCallback(
    (_: string, newValue: number) => {
      if (propOnChangeTab) {
        propOnChangeTab(_, newValue)
      } else {
        setInternalPetIndex(newValue)
      }
    },
    [propOnChangeTab]
  )

  return isLoading || !isSuccess ? (
    <SkeletonLoader />
  ) : (
    <RequiredAddonsInner offers={offers} petIndex={petIndex} onChangeTab={onChangeTabHandler} />
  )
}
